import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../CSS/HomePage.css';

interface Placement {
  placementId: number;
  placementTitle: string;
  placementSource: string;
  placementPicture: string;
  createdDate: string;
  lastUpdated: string;
  placementShortDesc: string;
  placementLongDesc: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [latestPlacements, setLatestPlacements] = useState<Placement[]>([]);
  const [isLoadingPlacements, setIsLoadingPlacements] = useState(true);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const testimonialRightRef = useRef<HTMLDivElement>(null);
  const [testimonialMinHeight, setTestimonialMinHeight] = useState<number>(0);

  // Testimonials data
  const testimonials = [
    {
      quote: "Myers McRae conducted a transparent and effective process allowing the search committee to focus on the candidates experience and fit",
      attribution: "Prominent HBCU"
    },
    {
      quote: "Myers McRae provided the leadership needed to bring together the diverse search committee members to a successful decision and selection of the next president",
      attribution: "Search Chair at a 4-year Institution in Florida"
    },
    {
      quote: "Myers McRae tailored their process to meet our aggressive timeline and achieved it",
      attribution: "Nursing College Dean"
    }
  ];

  // Healthcare logos
  const healthcareLogos = [
    'auburn-univ-logo.png',
    'fgcu-univ-logo.png',
    'gannon-univ-logo.png',
    'georgia-univ-logo.png',
    'idaho-univ-logo.png',
    'methodist-univ-logo.png',
    'mississippi-college-logo.png',
    'mississppi-univ-logo.png',
    'montefiore-univ-logo.png',
    'mtsuv-univ-logo.png',
    'pennstate-univ-logo.png',
    'purdue-univ-logo.png',
    'texas-univ-logo.png',
    'the-citadel-univ-logo.png',
    'umass-univ-logo.png',
    'university-of-texas-logo.png',
    'utahstate-univ-logo.png',
    'wake-forest-univ-logo.png'
  ];

  // Higher Education logos
  const higherEducationLogos = [
    'atm-univ-logo.png',
    'bethune-cookman-univ-logo.png',
    'bigsandy-college-logo.png',
    'broward-college-logo.png',
    'carroll-univ-logo.png',
    'coker-univ-logo.png',
    'college-of-charleston-univ-logo.png',
    'columbus-univ-logo.png',
    'dakota-univ-logo.png',
    'famu-univ-logo.png',
    'fgcu-univ-logo.png',
    'florida-polytechnic-univ-logo.png',
    'florida-univ-logo.png',
    'fort-valley-univ-logo.png',
    'gannon-univ-logo.png',
    'gardner-webb-univ-logo.png',
    'go-west-uwg-univ-logo.png',
    'gordon-univ-logo.png',
    'idaho-univ-logo.png',
    'ivy-tech-univ-logo.png',
    'kentucky-univ-logo.png',
    'kidspeace-univ-logo.png',
    'middle-georgia-univ-logo.png',
    'middle-georgia-univ-logo 2.png',
    'montanatech-univ-logo.png',
    'northwest-florida-college-logo.png',
    'pennstate-univ-logo.png',
    'pfeiffer-univ-logo.png',
    'phsc-univ-logo.png',
    'pimacommunity-college-logo.png',
    'purdue-univ-logo.png',
    'rhode-univ-logo.png',
    'savannah-univ-logo.png',
    'state-arkansas-univ-logo.png',
    'und-univ-logo.png',
    'university-of-alabama-logo.png',
    'university-of-central-florida-logo.png',
    'university-of-montana-logo.png',
    'university-of-south-carolina-univ-logo.png',
    'utah-valley-univ-logo.png',
    'winthrop-univ-logo.png'
  ];

  // Combine all logos
  const allLogos = [
    ...healthcareLogos.map(logo => ({ path: `/assets/Web Version/Who We Serve/Healthcare/${logo}`, name: logo })),
    ...higherEducationLogos.map(logo => ({ path: `/assets/Web Version/Who We Serve/Higher Education/${logo}`, name: logo }))
  ];

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setIsPaused(true);
    if (carouselRef.current) {
      setStartX(e.pageX - carouselRef.current.offsetLeft);
      setScrollLeft(carouselRef.current.scrollLeft);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    // Resume auto-scroll after a delay
    setTimeout(() => setIsPaused(false), 2000);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = scrollLeft - walk;
      checkScrollPosition();
    }
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    // Resume auto-scroll after a delay
    setTimeout(() => setIsPaused(false), 2000);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setIsPaused(true);
    if (carouselRef.current) {
      setStartX(e.touches[0].pageX - carouselRef.current.offsetLeft);
      setScrollLeft(carouselRef.current.scrollLeft);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !carouselRef.current) return;
    const x = e.touches[0].pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = scrollLeft - walk;
      checkScrollPosition();
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    // Resume auto-scroll after a delay
    setTimeout(() => setIsPaused(false), 2000);
  };

  const checkScrollPosition = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkScrollPosition();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        carousel.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, []);

  // Auto-scroll functionality
  useEffect(() => {
    if (isPaused || isDragging) {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      return;
    }

    autoScrollIntervalRef.current = setInterval(() => {
      if (carouselRef.current) {
        const carousel = carouselRef.current;
        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
        
        if (carousel.scrollLeft >= maxScroll - 1) {
          // Reset to beginning when reaching the end
          carousel.scrollLeft = 0;
        } else {
          // Scroll forward - increased from 1 to 2 for faster scrolling
          carousel.scrollLeft += 2;
        }
        checkScrollPosition();
      }
    }, 20); // Adjust speed by changing interval (lower = faster)

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [isPaused, isDragging]);

  // Fetch latest placements
  useEffect(() => {
    const fetchLatestPlacements = async () => {
      try {
        setIsLoadingPlacements(true);
        // Production API endpoint
        const response = await fetch('/api/placements');
        // const response = await fetch('http://localhost:3060/api/placements');
        const data = await response.json();

        if (data.success && data.data) {
          // Sort by createdDate descending and take top 4
          const sortedPlacements = [...data.data].sort((a, b) => {
            const dateA = new Date(a.createdDate).getTime();
            const dateB = new Date(b.createdDate).getTime();
            return dateB - dateA; // Most recent first
          });
          setLatestPlacements(sortedPlacements.slice(0, 4));
        } else {
          setLatestPlacements([]);
        }
      } catch (error) {
        console.error('Error fetching placements:', error);
        setLatestPlacements([]);
      } finally {
        setIsLoadingPlacements(false);
      }
    };

    fetchLatestPlacements();
  }, []);

  // Testimonial carousel auto-advance
  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonialIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 10000); // 10 seconds

    return () => {
      clearInterval(testimonialInterval);
    };
  }, [testimonials.length]);

  // Calculate and set minimum height for testimonial section to prevent height shifts
  useEffect(() => {
    const calculateMaxHeight = () => {
      if (!testimonialRightRef.current) return;

      // Temporarily render all testimonials to measure their heights
      const measurements: number[] = [];
      
      testimonials.forEach((_, index) => {
        // Create a temporary container with the same styling
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style.width = testimonialRightRef.current?.offsetWidth + 'px' || '500px';
        tempContainer.style.display = 'flex';
        tempContainer.style.flexDirection = 'column';
        tempContainer.className = 'testimonial-right';
        document.body.appendChild(tempContainer);

        // Create quote element
        const quote = document.createElement('blockquote');
        quote.className = 'testimonial-quote';
        quote.textContent = `"${testimonials[index].quote}"`;
        quote.style.margin = '0 0 2rem 0';
        quote.style.padding = '0';
        quote.style.border = 'none';
        quote.style.fontFamily = "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif";
        quote.style.fontSize = '1.3rem';
        quote.style.fontWeight = '400';
        quote.style.color = '#ffffff';
        quote.style.lineHeight = '1.6';

        // Create divider
        const divider = document.createElement('div');
        divider.className = 'testimonial-divider';
        divider.style.width = '100%';
        divider.style.height = '1px';
        divider.style.margin = '0 0 1.5rem 0';
        divider.style.backgroundColor = '#ffffff';

        // Create attribution
        const attribution = document.createElement('div');
        attribution.className = 'testimonial-attribution';
        const name = document.createElement('p');
        name.className = 'testimonial-name';
        name.textContent = testimonials[index].attribution;
        name.style.fontFamily = "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif";
        name.style.fontSize = '1.3rem';
        name.style.fontWeight = '700';
        name.style.color = '#ffffff';
        name.style.margin = '0';
        attribution.appendChild(name);

        tempContainer.appendChild(quote);
        tempContainer.appendChild(divider);
        tempContainer.appendChild(attribution);

        measurements.push(tempContainer.offsetHeight);
        document.body.removeChild(tempContainer);
      });

      if (measurements.length > 0) {
        const maxHeight = Math.max(...measurements);
        setTestimonialMinHeight(maxHeight);
      }
    };

    // Calculate after component mounts and on resize
    const timeoutId = setTimeout(calculateMaxHeight, 100);
    window.addEventListener('resize', calculateMaxHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, [testimonials]);

  // Helper function to convert database file path to API endpoint URL
  const getImageUrl = (path: string | null | undefined): string => {
    if (!path || path.trim() === '') return '/placeholder-image.jpg';
    // If path already starts with http, return as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // Convert database path to API endpoint
    // Database path: Files/Placement Images/filename.png
    // API endpoint: /api/placements/file/Files/Placement Images/filename.png
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    // Production API endpoint
    return `/api/placements/file/${cleanPath}`;
    // return `http://localhost:3060/api/placements/file/${cleanPath}`;
  };

  return (
    <div className="homepage">
      <div className="hero-banner">
        <video 
          className="hero-video" 
          autoPlay 
          loop 
          muted 
          playsInline
        >
          <source 
            src="/assets/Web Version/Home Page/90933-629483642_medium.mp4" 
            type="video/mp4" 
          />
        </video>
        <div className="homepage-hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            Empowering the Future of Higher Education
          </h1>
          <p className="hero-subtitle">
            Innovative Talent, Technology, and Workforce Solutions for Colleges and Universities
          </p>
          <div className="hero-buttons">
            <button 
              className="btn btn-primary"
              onClick={() => {
                const element = document.querySelector('.quick-service-highlights-title');
                if (element) {
                  // Get the header height to account for fixed header
                  const header = document.querySelector('.header');
                  const headerHeight = header ? header.getBoundingClientRect().height : 0;
                  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                  const offsetPosition = elementPosition - headerHeight;
                  
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }}
            >
              Discover Our Services
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/contact-us')}
            >
              Partner With Us
            </button>
            <button 
              className="btn btn-secondary btn-mobile-only"
              onClick={() => {
                navigate('/current-searches');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              See Current Searches
            </button>
          </div>
        </div>
      </div>

      <div className="who_we_serve">
        <p className="who_we_serve-text">
          Over the past four decades, Myers McRae has earned a national reputation for excellence, consistently identifying and recruiting talented leaders for higher education, not-for-profit organizations, corporations and businesses, and governmental agencies. Our services are designed to help institutions stay competitive in a rapidly changing world—connecting exceptional leaders, preparing students for the workforce, and aligning academic programs with real-world needs.
        </p>
        <div className="carousel-wrapper">
          {showLeftFade && <div className="carousel-fade carousel-fade-left"></div>}
          <div 
            className="logo-carousel"
            ref={carouselRef}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onScroll={checkScrollPosition}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            {allLogos.map((logo, index) => (
              <div key={index} className="logo-item">
                <img 
                  src={encodeURI(logo.path)} 
                  alt={logo.name.replace(/-/g, ' ').replace('.png', '')}
                  className="logo-image"
                  draggable="false"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    // Prevent click events during drag
                    if (isDragging) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  onError={(e) => {
                    // Fallback for logos with spaces in filename
                    const target = e.target as HTMLImageElement;
                    target.src = logo.path.replace(' ', '%20');
                  }}
                  style={{ 
                    pointerEvents: isDragging ? 'none' : 'auto',
                    userSelect: 'none'
                  }}
                />
              </div>
            ))}
          </div>
          {showRightFade && <div className="carousel-fade carousel-fade-right"></div>}
        </div>
      </div>
      <div className="home-page-services-container">
      <div className="home-page-picture-text-pair">
        <h2 className="quick-service-highlights-title">Quick Service Highlights</h2>
        <div className="homepage-picture-left-text-right">
          <div className="service-image-container">
            <img 
              src="/assets/Web Version/Home Page/home-executive-search-placement-services-section-image.png"
              alt="Executive Search and Placement Services"
              className="service-image"
            />
          </div>
          <div className="service-text-container">
            <h3 className="service-title">Executive Search and Placement Services</h3>
            <p className="service-description">
              Myers McRae specializes in identifying and recruiting exceptional leaders for higher education and nonprofit organizations. Our comprehensive search process combines strategic insight, personalized service, and innovative tools to deliver candidates who align with your mission and culture.
            </p>
            <button 
              className="service-button"
              onClick={() => {
                navigate('/services/executive-search');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Learn more about our Executive Search and Placement Services &gt;
            </button>
          </div>
        </div>

        <div className="homepage-picture-right-text-left">
          <div className="service-text-container">
            <h3 className="service-title">AI Innovation & Academic Modernization</h3>
            <p className="service-description">
              Prepare your institution for the future economy. We offer a full spectrum of AI services, from integrating <strong>cutting-edge AI curriculum</strong> and <strong>hands-on labs</strong> for student readiness to establishing high-impact, commercial-ready <strong>Healthcare AI Imaging Labs</strong> through our joint venture with CRO partner. We turn strategy into applied innovation.
            </p>
            <button className="service-button"
             onClick={() => {
              navigate('/services/ai-innovation');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              Explore Our Full AI & Academic Modernization Services &gt;
            </button>
          </div>
          <div className="service-image-container">
            <img 
              src="/assets/Web Version/Home Page/home-ai-innovation-academic-modernization-section-image.png"
              alt="AI Innovation & Academic Modernization"
              className="service-image"
            />
          </div>
        </div>

        <div className="homepage-picture-left-text-right">
          <div className="service-image-container">
            <img 
              src="/assets/Web Version/Home Page/home-campus-safety-audits-section-image.png"
              alt="Campus Safety Audits"
              className="service-image"
            />
          </div>
          <div className="service-text-container">
            <h3 className="service-title">Campus Safety Audits: From Risk to Resilience</h3>
            <p className="service-description">
              Protecting your campus is paramount. In addition to our renowned executive search and leadership consulting, Myers McRae now offers comprehensive <strong>Campus Safety Audits</strong> tailored for higher education institutions. We systematically evaluate your facilities, policies, and emergency preparedness to ensure <strong>Clery compliance</strong> and <strong>operational continuity</strong>. Get actionable insights to build a strong, proactive culture of safety that safeguards your entire community.
            </p>
            <button className="service-button"
             onClick={() => {
              navigate('/services/campus-safety');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              Learn More About Safety Audits &gt;
            </button>
          </div>
        </div>

        <div className="homepage-picture-right-text-left">
          <div className="service-text-container">
            <h3 className="service-title">Executive Coaching for Academic Leaders</h3>
            <p className="service-description">
              Myers McRae offers personalized executive coaching for presidents, provosts, deans, and senior administrators in higher education. Led by <strong>experienced former academic executives</strong>, our coaching strengthens self-awareness, decision-making, and strategic impact—enabling them to guide their institutions with <strong>clarity, confidence, and purpose.</strong>
            </p>
            <button className="service-button"
             onClick={() => {
              navigate('/services/executive-coaching');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              Learn more about our Executive Coaching for Academic Leaders &gt;
            </button>
          </div>
          <div className="service-image-container">
            <img 
              src="/assets/Web Version/Home Page/home-executive-coaching-section-image.png"
              alt="Executive Coaching for Academic Leaders"
              className="service-image"
            />
          </div>
        </div>
      </div>
      </div>

      <div className="homepage-featured-success-stories">
        <h2 className="featured-success-stories-title">Featured Success Stories</h2>
        <div className="success-stories-container">
          <div className="success-story-card">
            <div className="success-story-image-container">
              <img 
                src="/assets/Web Version/Home Page/regional-internship-programs-success-story-image.png"
                alt="Regional Internship Programs Success Story"
                className="success-story-image"
              />
            </div>
            <div className="success-story-content">
              <h3 className="success-story-title">Regional Internship Programs</h3>
              <p className="success-story-description">
                Our innovative regional internship programs connect students with real-world opportunities, bridging the gap between academic learning and professional experience. Through strategic partnerships with leading organizations, we've created pathways that prepare students for successful careers.
              </p>
              <button 
                className="success-story-button"
                onClick={() => {
                  navigate('/news-and-insights/our-success-stories/regional-internship');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Read Full Story &gt;
              </button>
            </div>
          </div>

          <div className="success-story-card">
            <div className="success-story-image-container">
              <img 
                src="/assets/Web Version/Home Page/empowering-universities-success-story-image.png"
                alt="Empowering Universities Success Story"
                className="success-story-image"
              />
            </div>
            <div className="success-story-content">
              <h3 className="success-story-title">Empowering Universities</h3>
              <p className="success-story-description">
                We've helped numerous universities transform their leadership and operations through comprehensive executive search and strategic consulting services. Our approach combines deep industry expertise with innovative solutions that drive institutional success.
              </p>
              <button 
                className="success-story-button"
                onClick={() => {
                  navigate('/news-and-insights/our-success-stories/empowering-lead');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Read Full Story &gt;
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="homepage-latest-news">
        <div className="latest-news-header">
          <h2 className="latest-news-title">Latest News</h2>
          <Link to="/news-and-insights/recent-placements" className="latest-news-view-all">View all &gt;</Link>
        </div>
        <div className="latest-news-container">
          {isLoadingPlacements ? (
            <div className="latest-news-loading">Loading latest news...</div>
          ) : latestPlacements.length === 0 ? (
            <div className="latest-news-empty">No recent placements available.</div>
          ) : (
            latestPlacements.map((placement) => (
              <div key={placement.placementId} className="news-card">
                <div className="news-image-container">
                  <img 
                    src={getImageUrl(placement.placementPicture)}
                    alt={placement.placementTitle}
                    className="news-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/placeholder-image.jpg';
                    }}
                  />
                </div>
                <div className="news-content">
                  <Link 
                    to={`/news-and-insights/recent-placements/${placement.placementId}`}
                    className="news-title-link"
                  >
                    <h3 className="news-title">{placement.placementTitle}</h3>
                  </Link>
                  <p className="news-description">{placement.placementShortDesc}</p>
                  <div className="news-institution">
                    <span className="institution-name">{placement.placementSource}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* <div className="homepage-testimonial">
        <div className="testimonial-container">
          <div className="testimonial-left">
            <p className="testimonial-callout">Don't take our word for it!</p>
            <p className="testimonial-subheading">
              <strong>Hear what our <span className="testimonial-customers">Clients</span> say</strong>
            </p>
          </div>
          <div 
            className="testimonial-right"
            ref={testimonialRightRef}
            style={{
              minHeight: testimonialMinHeight > 0 ? `${testimonialMinHeight}px` : 'auto'
            }}
          >
            <blockquote 
              className="testimonial-quote"
              key={currentTestimonialIndex}
              style={{
                animation: 'fadeIn 0.5s ease-in'
              }}
            >
              "{testimonials[currentTestimonialIndex].quote}"
            </blockquote>
            <div className="testimonial-divider"></div>
            <div 
              className="testimonial-attribution"
              key={`attribution-${currentTestimonialIndex}`}
              style={{
                animation: 'fadeIn 0.5s ease-in'
              }}
            >
              <p className="testimonial-name">{testimonials[currentTestimonialIndex].attribution}</p>
            </div>
          </div>
        </div>
      </div> */}

      <div className="homepage-request">
        <div className="request-container">
          <div className="request-image-container">
            <img 
              src="/assets/Web Version/Home Page/request-a-consultation-section-image.png"
              alt="Request a Consultation"
              className="request-image"
            />
          </div>
          <div className="request-content">
            <h2 className="request-title">Request a Consultation</h2>
            <p className="request-description">
              Connect with our Senior Search Consultants. We are a bespoke executive placement agency that identifies and secures proven leaders for top-tier companies. Let us introduce you to the talent ready to execute your future strategy.
            </p>
            <button 
              className="request-button"
              onClick={() => navigate('/contact-us')}
            >
              Contact Us Today
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;

